import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aurora page header — the canonical "page title + lede + actions" surface.
 *
 * Replaces the inline `<h1 className="text-3xl font-bold tracking-tight">`
 * pattern scattered across routes. Establishes a single rhythm and gives us
 * one place to evolve the typography ramp.
 *
 * Slots:
 * - `eyebrow`  — small caps label above the title (e.g. "Documents")
 * - `title`    — required
 * - `description` — body lede under the title
 * - `actions`  — right-aligned action cluster
 *
 * Headings render at `text-[1.75rem]` (28px) — restrained vs. the previous
 * 30px text-3xl. Compressed display tracking comes from the global CSS
 * applied to h1 in index.css.
 */
interface PageHeaderProps extends Omit<React.ComponentProps<'header'>, 'title'> {
  eyebrow?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}

function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
  ...props
}: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 space-y-1.5">
        {eyebrow ? <div className="aurora-eyebrow">{eyebrow}</div> : null}
        <h1 className="text-[1.75rem] font-semibold leading-[1.1] tracking-tight text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  )
}

/**
 * Section header — for subdivisions inside a page. Smaller than PageHeader,
 * meant to title a single section card or a grid of cards.
 */
interface SectionHeaderProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}

function SectionHeader({
  title,
  description,
  actions,
  className,
  ...props
}: SectionHeaderProps) {
  return (
    <div
      data-slot="section-header"
      className={cn(
        'flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 space-y-0.5">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {title}
        </h2>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export { PageHeader, SectionHeader }
